import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { get, getDatabase, ref, set } from 'firebase/database'
import fbConf from '../../FirebaseConf'
import Comm from '../../Comm'
import Table from '../../modules/Table'
import { TableCellTypes } from '../../enums/TableCellTypes'
import Dropdown from '../../components/Dropdown'
import ButtonWithIcon from '../../components/ButtonWithIcon'
import Button from '../../components/Button'
import Select from '../../components/Select'
import Modal from '../../components/Modal'
import InputWithLabel from '../../components/InputWithLabel'
import Icon from '../../Icon'
import WhiteSpace from '../../components/Whitespace'
import { useNotification } from '../../modules/Notification'
import { UserTabProps } from './types'
import { CardTitle, MutedValue, ModalPad, ModalCloseButtonContainer, ModalTitle, ModalButtonRow, IconButton, ChargerNameTop, ChargerNameSub } from './userStyles'
import { accessLevelLabelKey, formatDate } from './userHelpers'

interface Enrichment {
  online: Set<string>
  groups: { [id: string]: string }
  meta: { [id: string]: { serial: string; reg: string } }
}

// Access-level buckets offered when pairing a charger to this user.
const PAIR_ROLES = [7, 2]

export default function UserChargers({ user, uid, refresh }: UserTabProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { addNotification } = useNotification()
  const db = getDatabase(fbConf)

  const [enrich, setEnrich] = useState<Enrichment | null>(null)
  const [triggerRefresh, setTriggerRefresh] = useState<number>(0)
  const [statusFilter, setStatusFilter] = useState<'All' | 'On'>('All')
  const [isPairing, setIsPairing] = useState<boolean>(false)
  const [pairId, setPairId] = useState<string>('')
  const [pairRole, setPairRole] = useState<number>(7)

  const chargerIds = Object.keys(user.chargers || {})

  useEffect(() => {
    let cancelled = false
    const result: Enrichment = { online: new Set(), groups: {}, meta: {} }
    // Apply progressively (fresh refs so the Table re-snapshots) instead of blocking on the slowest call.
    const apply = () => {
      if (cancelled) return
      setEnrich({ online: new Set(result.online), groups: { ...result.groups }, meta: { ...result.meta } })
      setTriggerRefresh(prev => prev + 1)
    }

    // Fast sources (online status + group membership) run in parallel and show immediately.
    Promise.allSettled([
      (async () => {
        const r = await Comm('/api-websocket/online', {})
        if (r.status === 200) { const body = await r.json(); if (Array.isArray(body)) body.forEach((id: string) => result.online.add(id)) }
      })(),
      (async () => {
        const snap = await get(ref(db, 'voltie/updategroups'))
        const groups = snap.val() || {}
        for (const groupName in groups) for (const id in (groups[groupName]?.chargers || {})) result.groups[id] = groupName
      })(),
    ]).then(apply)

    // Serial + registration date come from the (large) all-chargers registry; fill in when it resolves.
    ;(async () => {
      try {
        const r = await Comm('/api-charger/chargers', {})
        if (r.status === 200) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const all: any = await r.json()
          if (Array.isArray(all)) for (const c of all) result.meta[c.chargerId] = { serial: c.serialNumber || '-', reg: c.registrationDate || '' }
        }
      } catch { /* leave empty */ }
      apply()
    })()

    return () => { cancelled = true }
  }, [uid])

  // Re-snapshot the table whenever the paired-charger set changes (Table only recomputes on triggerRefresh).
  useEffect(() => {
    setTriggerRefresh(prev => prev + 1)
  }, [chargerIds.length])

  async function pairCharger() {
    const id = pairId.trim()
    if (!id) return
    try {
      await set(ref(db, `voltie/chargerusers/${uid}/chargers/${id}`), {
        access_lvl: pairRole,
        hostname: `voltiecharger-${id.slice(-4)}`,
        name: `voltiecharger-${id.slice(-4)}`,
      })
      addNotification(t('user_saved'), 'TEXT', 'normal', 1500)
      setIsPairing(false)
      setPairId('')
      refresh()
      setTriggerRefresh(prev => prev + 1)
    } catch (e) { addNotification(`${e}`, 'TEXT', 'error') }
  }

  const displayed = chargerIds.filter(id => statusFilter === 'All' || enrich?.online.has(id))

  const STATUS_FILTERS = [
    { value: 'All' as const, label: t('user_filterAll') },
    { value: 'On' as const, label: t('user_filterOnline') },
  ]
  const currentStatusLabel = STATUS_FILTERS.find(f => f.value === statusFilter)?.label || t('user_filterAll')
  const filterSlot = (
    <Dropdown
      title={{ icon: 'general.filter', label: currentStatusLabel }}
      options={STATUS_FILTERS.map(f => ({ label: f.label }))}
      handleChoose={(label) => { const f = STATUS_FILTERS.find(x => x.label === label); if (f) { setStatusFilter(f.value); setTriggerRefresh(prev => prev + 1) } }}
    />
  )

  return (
    <>
      {isPairing && (
        <Modal onEscape={() => setIsPairing(false)}>
          <ModalPad role="dialog" aria-modal="true" aria-labelledby="pair-title" style={{ width: 'min(400px, calc(100vw - 72px))' }}>
            <ModalCloseButtonContainer>
              <IconButton aria-label={t('user_cancel')} onClick={() => setIsPairing(false)}>
                <Icon path="general.x" width="20px" height="20px" />
              </IconButton>
            </ModalCloseButtonContainer>
            <ModalTitle id="pair-title">{t('user_pairCharger')}</ModalTitle>
            <InputWithLabel label={t('user_pairChargerIdPlaceholder')} width="100%" value={pairId} onInput={setPairId} autoFocus />
            <WhiteSpace height="16px" />
            <Select width="100%" label={t('user_pairRole')} options={PAIR_ROLES.map(r => t(accessLevelLabelKey(r)))} handleSelect={(label) => {
              const found = PAIR_ROLES.find(r => t(accessLevelLabelKey(r)) === label)
              if (found != null) setPairRole(found)
            }} />
            <ModalButtonRow>
              <Button label={t('user_cancel')} width="calc(50% - 6px)" onclick={() => { setIsPairing(false); setPairId('') }} bgcolor="transparent" labelcolor="black" />
              <Button label={t('user_pairConfirm')} width="calc(50% - 6px)" onclick={pairCharger} />
            </ModalButtonRow>
          </ModalPad>
        </Modal>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <CardTitle style={{ margin: 0 }}>{t('user_chargers')}</CardTitle>
        <ButtonWithIcon icon="general.plus" label={t('user_pairCharger')} onClick={() => setIsPairing(true)} backgroundColor="dark" iconColor="white" />
      </div>

      {chargerIds.length === 0 ? (
        <MutedValue>{t('user_noChargers')}</MutedValue>
      ) : (
        <Table
          id="UsersChargerTab"
          height="calc(90vh - 195px)"
          isSearchEnabled={true}
          isPaginationEnabled={true}
          searchBarSlot={filterSlot}
          isRenderEmpty={displayed.length === 0}
          triggerRefresh={triggerRefresh}
          columns={[
            { width: '44px' },
            { label: t('user_colChargerName'), width: '340px' },
            { label: t('user_colGroup'), width: '15%' },
            { label: t('user_colStatus'), width: '13%' },
            { label: t('user_colPermission'), width: '15%' },
            { label: t('user_colRegDate'), width: '15%' },
            { label: t('user_colSerial'), width: '14%' },
            { width: '44px' },
          ]}
          data={displayed.length > 0 ? displayed.map(id => {
            const c = user.chargers![id]
            const online = enrich?.online.has(id)
            const meta = enrich?.meta[id]
            const link = `/chargers/${id}`
            return [
              { link, value: { type: TableCellTypes.Image, src: online ? 'chargers.chargerOnline' : 'chargers.chargerOffline' } },
              { link, sortBy: c.name || id, custom: <div><ChargerNameTop title={c.name || id}>{c.name || id}</ChargerNameTop><ChargerNameSub title={id}>{id}</ChargerNameSub></div> },
              { link, sortBy: enrich?.groups[id] || '', value: { type: TableCellTypes.Label, value: enrich?.groups[id] || '-' } },
              { link, sortBy: online ? 1 : 0, value: { type: TableCellTypes.Status, label: online ? t('charger_online') : t('charger_offline'), themeColor: online ? 'success' : 'gray_400' } },
              { link, sortBy: c.access_lvl ?? 0, value: { type: TableCellTypes.Status, label: t(accessLevelLabelKey(c.access_lvl)), themeColor: 'primary' } },
              { link, sortBy: meta?.reg || '', value: { type: TableCellTypes.Label, value: meta?.reg ? formatDate(meta.reg) : '-' } },
              { link, sortBy: meta?.serial || '', value: { type: TableCellTypes.Label, value: meta?.serial || '-' } },
              { custom: <Dropdown title={{ icon: 'general.more', label: '' }} options={[{ icon: 'general.log', label: t('chargers_details') }]} handleChoose={() => navigate(`/chargers/${id}`)} showOptionContainerOnLeft /> },
            ]
          }) : [[{}, { sortBy: '' }, { sortBy: '' }, { sortBy: 0 }, { sortBy: 0 }, { sortBy: '' }, { sortBy: '' }, {}]]}
        />
      )}
    </>
  )
}
